import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Search, Sparkles, User, Settings, LogOut, ListChecks, CreditCard, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import AccountBanner from "@/components/AccountBanner";
import { BillingDialogsProvider } from "@/contexts/BillingDialogs";

const COLLAPSE_KEY = "il_sidebar_collapsed";

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <BillingDialogsProvider>
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <Sidebar
        onOpenCommand={() => setOpen(true)}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <AccountBanner />
        <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      </main>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages, companies, actions…" data-testid="command-input" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigate">
            <CommandItem onSelect={() => go("/research")}>
              <Search className="h-4 w-4" /> Research
            </CommandItem>
            <CommandItem onSelect={() => go("/enrich")}>
              <Sparkles className="h-4 w-4" /> Enrich
            </CommandItem>
            <CommandItem onSelect={() => go("/lists")}>
              <ListChecks className="h-4 w-4" /> Lists
            </CommandItem>
            <CommandItem onSelect={() => go("/profile")}>
              <User className="h-4 w-4" /> Profile
            </CommandItem>
            <CommandItem onSelect={() => go("/billing")}>
              <CreditCard className="h-4 w-4" /> Billing
            </CommandItem>
            {user?.role === "admin" && (
              <CommandItem onSelect={() => go("/admin")}>
                <ShieldCheck className="h-4 w-4" /> Admin
              </CommandItem>
            )}
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Actions">
            <CommandItem onSelect={() => go("/profile")}>
              <Settings className="h-4 w-4" /> Settings
            </CommandItem>
            <CommandItem
              onSelect={async () => {
                setOpen(false);
                await logout();
                navigate("/login");
              }}
            >
              <LogOut className="h-4 w-4" /> Log out
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </div>
    </BillingDialogsProvider>
  );
}
